import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useConfig } from 'hooks/use-config';
import { useCommonStore } from 'state/store/common/useCommonStore';
import { 
  getKnowledgeBase, 
  upsertKnowledgeBase, 
  transformApiDataToForm, 
  updateReferenceSetting
} from '../services/knowledge-base-service';
import { KnowledgeBaseFormData } from '../types/knowledge-base';
import { useToast } from 'hooks/use-toast';
import { useTranslation } from 'react-i18next';

/**
 * Query key for knowledge base data
 */
const KNOWLEDGE_BASE_QUERY_KEY = ['knowledgeBase'];

/**
 * Hook to fetch user's knowledge base data
 */
export const useKnowledgeBase = () => {
  const { configurationSettings } = useConfig();
  return useQuery({
    queryKey: KNOWLEDGE_BASE_QUERY_KEY,
    queryFn: async () => {
      const data = await getKnowledgeBase(configurationSettings);
      console.log(26, data)
      return data ? transformApiDataToForm(data) : null;
    },
    enabled: !!configurationSettings,
    
  });
};

/**
 * Hook to update knowledge base data
 */
export const useUpdateKnowledgeBase = () => {
  const { configurationSettings } = useConfig();
  const queryClient = useQueryClient();
  const user = useCommonStore((state) => state.user);

  return useMutation({
    mutationFn: async (formData: KnowledgeBaseFormData) => {
      if (!user?.id) {
        throw new Error('User ID is required');
      }
      return upsertKnowledgeBase(formData, user.id, configurationSettings);
    },
    onSuccess: (data) => {
      // Invalidate and refetch knowledge base data
      queryClient.invalidateQueries({ queryKey: KNOWLEDGE_BASE_QUERY_KEY });
      
      // Optionally update the cache immediately with the new data
      if (data) {
        const formData = transformApiDataToForm(data);
        queryClient.setQueryData(KNOWLEDGE_BASE_QUERY_KEY, formData);
      }
    },
    onError: (error) => {
      console.error('Failed to update knowledge base:', error);
    },
  });
};

/**
 * Hook to invalidate knowledge base cache
 */
export const useInvalidateKnowledgeBase = () => {
  const queryClient = useQueryClient();

  return () => {
    queryClient.invalidateQueries({ queryKey: KNOWLEDGE_BASE_QUERY_KEY });
  };
};

export const useUpdateReferenceSetting = () => {
  const { configurationSettings } = useConfig();
  const { toast } = useToast();
  const { t } = useTranslation();
  
  return useMutation({
    mutationFn: async ({ 
      setting, 
      value 
    }: { 
      setting: 'reference_chat_history' | 'reference_saved_memories'; 
      value: boolean; 
    }) => {
      await updateReferenceSetting(setting, value, configurationSettings);
    },
    onSuccess: (_, { setting }) => {
      const settingName = setting === 'reference_chat_history' ? 'chat history' : 'saved memories';
      console.log(`Reference ${settingName} updated successfully`);
    },
    onError: (error, { setting }) => {
      const settingName = setting === 'reference_chat_history' ? 'chat history' : 'saved memories';
      console.error(`Failed to update reference ${settingName}:`, error);
      toast({
        title: t('FAILED'),
        description: setting === 'reference_chat_history' 
          ? t('FAILED_TO_UPDATE_REFERENCE_CHAT_HISTORY')
          : t('FAILED_TO_UPDATE_REFERENCE_SAVED_MEMORIES'),
        variant: 'destructive',
      });
    },
  });
};

// Keep the old hooks for backward compatibility if needed elsewhere
export const useUpdateReferenceChatHistory = () => {
  const updateReferenceSetting = useUpdateReferenceSetting();
  
  return {
    ...updateReferenceSetting,
    mutateAsync: (referenceChatHistory: boolean) => 
      updateReferenceSetting.mutateAsync({ setting: 'reference_chat_history', value: referenceChatHistory })
  };
};

export const useUpdateReferenceSavedMemories = () => {
  const updateReferenceSetting = useUpdateReferenceSetting();
  
  return {
    ...updateReferenceSetting,
    mutateAsync: (referenceSavedMemories: boolean) => 
      updateReferenceSetting.mutateAsync({ setting: 'reference_saved_memories', value: referenceSavedMemories })
  };
};